import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import ProcessContext from '../../../contexts/process/ProcessContext';
import UIContext from '../../../contexts/ui/UIContext';
import AuthContext from '../../../contexts/auth/AuthContext';
import WeighingPanel from './WeighingPanel';
import WeighingTray from './WeighingTray';
import Loading from '../../ui-elements/Loading';
import StatusBarButton from '../../layout/StatusBarButton';
import BackButton from '../../layout/BackButton';
import Scale from './Scale';
import { HttpAgent } from '../../../utility/HttpAgent';
import { HELP_DESK_LINK } from '../../../constants/helpDesk';
import { useSignalRContext } from '../../../contexts/signalR/SignalRProvider';
import LabelPrinterTemplateApiService from '../../../services/apiServices/LabelPrinterTemplateApiService';
import { useNavigate } from 'react-router-dom';
import HarvestingProceduresContext from '../../../contexts/management/harvestingProcedures/HarvestingProceduresContext';

const WeighingContainer = ({ completingRef }) => {
  const {
    activeProcessId,
    activeProcessType,
    processTypes,
    taskData,
    updateOccupancy,
    continueProcess,
    additionalProcessInformation,
    loadOccupancy,
    notifySuccess,
    notifyError,
    requestedInformationType,
    occupancyArray,
    updateProcessState,
    selection,
    finishProcess
  } = useContext(ProcessContext);
  const { loading, setInfo, clearInfo } = useContext(UIContext);
  const { currentTenant, currentContainer } = useContext(AuthContext);
  const { allHarvestingProcedures } = useContext(HarvestingProceduresContext)
  const [cameraState, setCameraState] = useState(false);
  const [weighedPlantDisplayText, setWeighedPlantDisplayText] = useState('');
  const [plantBlockBeingWeighed, setPlantBlockBeingWeighed] = useState(null);
  const [backIsForbidden, setBackIsForbidden] = useState(false);
  const navigate = useNavigate();

  const cameraStateUrl = `/Api/V1/Tenants/${currentTenant?.id}/Containers/${currentContainer?.id}/Devices/${currentContainer.weighingCameraId}/State`;

  const { registerClientFunction, unregisterClientFunction, signalRIsReady } = useSignalRContext();
  const initialWeighingData = {
    weight: 0.0,
    timestamp: new Date().toISOString(),
    alibiIdentifier: "",
    scaleId: currentContainer?.scaleId,
    weighingCameraOperationId: null,
  };

  const [processedWeighingData, setProcessedWeighingData] = useState(initialWeighingData);

  // Use ref for isWaitingForZero to persist across renders without triggering a re-render
  const isWaitingForZeroRef = useRef(false);

  const safeRedirect = () => {

    if (taskData?.redirectUrl) {
      window.location.href(taskData.redirectUrl);
      return;
    }

    if (taskData) {
      navigate('/', { state: { slide: 1 } });
    } else {
      navigate('/', { state: { slide: 0 } });
    }
  };

  const processWeightUpdate = async (weightUpdate) => {
    const { weight, timestamp, alibiIdentifier, weighingCameraOperationId, scaleId } = weightUpdate;
    // Check is Zero required
    if (isWaitingForZeroRef.current) {
      if (weight === 0) {
        isWaitingForZeroRef.current = false;
        resetWeighingData();
      }
    } else {
      // We're no longer waiting for zero
      if (weight > 0) {
        // Update processedWeighingData with new weight data
        setProcessedWeighingData({
          weight,
          timestamp,
          alibiIdentifier,
          scaleId,
          weighingCameraOperationId,
        });
        // Start waiting for zero again
        isWaitingForZeroRef.current = true;
      }
    }
  };

  const updateProcessStepViaSignalR = async (responseData) => {
    const { availableInformation, processFinished, requestedInformation, type, backIsAllowed } = responseData;
    if (processFinished && !requestedInformation) {
      notifySuccess('Process finished successfully');
      updateProcessState({availableInformation, processFinished, requestedInformation, type, backIsAllowed});
      if (completingRef && !completingRef.current) {
        completingRef.current = true;
        try {
          await finishProcess();
        } catch (err) {
          console.error('finishProcess failed', err);
        } finally {
          safeRedirect();
        }
      } else {

        safeRedirect();
      }
      return;
    }

    else if (requestedInformation?.type === 14) {
      notifySuccess('Plant harvested')
      updateProcessState({availableInformation, processFinished, requestedInformation, type, backIsAllowed});
    }
    else if (activeProcessType === 4 && requestedInformation?.type === 4) {
      notifySuccess('Plant weighed')
      updateProcessState({availableInformation, processFinished, requestedInformation, type, backIsAllowed});
    }
  }
  useEffect(() => {
    clearInfo()

    let rawProcessTypeKey = ""
    if (typeof activeProcessType === "string") {
      rawProcessTypeKey = activeProcessType
    } else if (typeof activeProcessType === "number") {
      const key = Object.keys(processTypes)
        .find(k => processTypes[k] === activeProcessType)
        rawProcessTypeKey = key || ""
    }
    const processType = (rawProcessTypeKey.replace(/ing$/i, "") || "—").toUpperCase()

    let harvestingProcedure
    if (processType === "HARVEST" && typeof selection === "string") {
      const chosenHarvestingProcedure = allHarvestingProcedures.find(hp => hp.id === selection)
      harvestingProcedure = chosenHarvestingProcedure
        ? chosenHarvestingProcedure.type.replace(/([A-Z])/g, " $1").trim()
        : "—"
    }

    let tray, panel
    if (processType === "HARVEST") {
      panel = additionalProcessInformation.sourceEntity
    } else {
      const src = additionalProcessInformation.sourceEntity || ""
      if (/^t/i.test(src)) {
        tray = src
      } else {
        panel = src
      }
    }
    const plantSlots = additionalProcessInformation.selectedPlantSlotOrdinalNumbers || []
    const plants = plantSlots.length

    setInfo({ processType, harvestingProcedure, tray, panel, plants })
  }, [])



  useEffect(() => {
    load();
    updateCameraState();

    return () => {
      updateOccupancy([]);
    };
  }, []);

  useEffect(() => {
    if (signalRIsReady) {
      registerClientFunction("scaleHub", "WeightUpdate", processWeightUpdate);
      registerClientFunction("scaleHub", "ProcessConfirmationStepResult", updateProcessStepViaSignalR);
    }

    return() => {
      if (signalRIsReady) {
        unregisterClientFunction("scaleHub", "WeightUpdate", processWeightUpdate);
        unregisterClientFunction("scaleHub", "ProcessConfirmationStepResult", updateProcessStepViaSignalR);
      }
    };
  }, [signalRIsReady]);

  useEffect(() => {
    let channelIndex = Math.floor(additionalProcessInformation?.plantSlotOrdinalNumber / 100);

    if (additionalProcessInformation?.sourceEntity.toLowerCase().startsWith('p') && occupancyArray[channelIndex] != undefined) {
      let elementBeingWeighed = occupancyArray[channelIndex].plantSlotBlocks.filter(
        (block) => block.startPlantSlotOrdinalNumber === additionalProcessInformation?.plantSlotOrdinalNumber
      );
      setPlantBlockBeingWeighed(elementBeingWeighed);
    }
  }, [additionalProcessInformation, cameraState]);

  const createWeighingBody = () => {
    const timestampCheck = processedWeighingData?.timestamp;
    return {
      weight: processedWeighingData?.weight,
      alibiIdentifier: processedWeighingData?.alibiIdentifier,
      timestamp: timestampCheck ? new Date(processedWeighingData?.timestamp).toISOString() : null,
      weighingCameraOperationId: processedWeighingData?.weighingCameraOperationId,
    };
  };

  const handleConfirm = async () => {
    try {
      setBackIsForbidden(true);
      const isProcessFinished = await continueProcess(activeProcessId, { ...createWeighingBody()});
      if (completingRef && isProcessFinished && !completingRef.current) {
        completingRef.current = true;
        await finishProcess()
      }
      if (isProcessFinished) {
        safeRedirect();
      }

      resetWeighingData();
    } catch (error) {
      notifyError(<>We have encountered an issue while confirming the process. Please contact the support team {HELP_DESK_LINK}.</>);
      console.error(error);
    }
  };

  const handleManualConfirm = async () => {
    isWaitingForZeroRef.current = false;
    handleConfirm();
  };

  const resetWeighingData = () => {
    setProcessedWeighingData(initialWeighingData);
  };

  const handlePrint = async () => {
    try {
      await LabelPrinterTemplateApiService.printLabelForProcess(currentTenant?.id, currentContainer?.id, activeProcessId);
      notifySuccess('Label printed');
    } catch (error) {
      notifyError(<>We have encountered an issue while printing the label. Please contact the support team {HELP_DESK_LINK}.</>);
      console.error(error);
    }
  };

  const load = async () => {
    if (additionalProcessInformation) {
      await loadOccupancy(additionalProcessInformation.sourceEntity, 'panel');
    }
  };

  const updateCameraState = () => {
    HttpAgent.get(cameraStateUrl).then((response) => {
      const newState = response.data ? true : false;
      setCameraState(newState);
    });
  };

  const renderDependingOnAnalysisState = () => {
    return (
      <Fragment>
        <BackButton disabled={backIsForbidden} />
        {additionalProcessInformation && !loading ? (
          additionalProcessInformation.sourceEntity.toLowerCase().startsWith('t') ? (
            <WeighingTray setScaleTitle={setWeighedPlantDisplayText} />
          ) : (
            <WeighingPanel setScaleTitle={setWeighedPlantDisplayText} />
          )
        ) : (
          <Loading fullScreen />
        )}
        <Scale
          title={weighedPlantDisplayText}
          weighingData={processedWeighingData}
          element={plantBlockBeingWeighed}
          layout={additionalProcessInformation && additionalProcessInformation.sourceEntity.toLowerCase().startsWith('t') ? 'scale-tray' : 'scale'}
        />
        {renderControlButtons()}
      </Fragment>
    );
  };

  const renderControlButtons = () => {

    if (requestedInformationType === 14) {
      return (
        <Fragment>
          <StatusBarButton
            label='Print'
            icon='fas fa-print'
            clickHandler={handlePrint}
            type='inline'
            statusSlot={4}
            disabled={!processedWeighingData?.weight}
          />
          <StatusBarButton
            label={'Confirm'}
            icon='fas fa-check'
            reversed={true}
            clickHandler={handleManualConfirm}
            statusSlot={5}
            disabled={!processedWeighingData?.weight}
            size={''}
          />
        </Fragment>
      );
    }

    if (requestedInformationType == 4) {
      return (
        <Fragment>
          <StatusBarButton
            label='Confirm'
            icon='fas fa-check'
            reversed={true}
            clickHandler={handleManualConfirm}
            statusSlot={5}
            disabled={!processedWeighingData?.weight}
          />
        </Fragment>
      );
    }
  };

  return renderDependingOnAnalysisState();
};

export default WeighingContainer;
